/**
 * Podíl spolupráce. Množství se dělí stejným dílem, výplata je díl × sazba příjemce.
 * Když sazbu neznáme, cena aktora se dělí rovnoměrně (haléře prvním příjemcům).
 */
export function workShareBillableQuantity(input: {
  metadataQuantity?: unknown;
  lengthM?: number | null;
  fullPrice?: number | null;
  actorUnitPrice?: number | null;
}): number | null {
  const fromMeta = positiveQuantity(input.metadataQuantity);
  if (fromMeta != null) return fromMeta;
  const length = positiveQuantity(input.lengthM);
  if (length != null) return length;
  const unit = nonNegative(input.actorUnitPrice);
  const full = nonNegative(input.fullPrice);
  if (unit != null && unit > 0 && full != null && full > 0) return full / unit;
  return null;
}

/**
 * Výplata jednoho příjemce. `unitPrice` je jeho jednotková sazba; `null` = rovnoměrný díl `fullPrice`.
 */
export function workShareRecipientPrice(input: {
  quantity: number | null;
  fullPrice: number;
  recipientCount: number;
  recipientIndex: number;
  unitPrice: number | null;
}): number {
  const count = input.recipientCount;
  if (count < 1) return 0;
  if (count === 1) return roundMoney(input.fullPrice);
  const unit = nonNegative(input.unitPrice);
  const qty = input.quantity;
  if (unit != null && qty != null && Number.isFinite(qty) && qty > 0) {
    return roundMoney((qty * unit) / count);
  }
  const parts = splitPriceEven(input.fullPrice, count);
  const index = input.recipientIndex;
  return parts[index] ?? 0;
}

/**
 * Rozdělení ceny výkazu práce mezi N osob (haléře; zbytek přidělí prvním příjemcům).
 * Záloha, když řádek nemá množství a sazbu příjemce.
 */
export function splitPriceEven(totalPrice: number, recipientCount: number): number[] {
  if (recipientCount < 1) return [];
  if (recipientCount === 1) return [roundMoney(totalPrice)];
  const cents = Math.round(totalPrice * 100);
  const baseCents = Math.floor(cents / recipientCount);
  const remainder = cents - baseCents * recipientCount;
  const out: number[] = [];
  for (let i = 0; i < recipientCount; i++) {
    const partCents = baseCents + (i < remainder ? 1 : 0);
    out.push(partCents / 100);
  }
  return out;
}

function roundMoney(n: number): number {
  return Math.round(n * 100) / 100;
}

function nonNegative(value: unknown): number | null {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

function positiveQuantity(value: unknown): number | null {
  const n = nonNegative(value);
  if (n == null || n <= 0) return null;
  return n;
}

/** Normalizace e-mailů pro metadata; odstraní prázdné a duplicity (case-insensitive). */
export function normalizeCollaboratorEmails(emails: unknown[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const e of emails) {
    const s = String(e ?? '').trim().toLowerCase();
    if (!s || seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out;
}

/**
 * Zaškrtnutí v modalu „Spolupráce“ → příjemci nového řádku výkazu.
 * Nezaškrtnutý e-mail (včetně aktora, který se odebral) v seznamu není.
 */
export function selectedCollaboratorEmails(
  rows: ReadonlyArray<{ email?: string | null }>,
  selected: Readonly<Record<string, boolean>>,
): string[] {
  const chosen: string[] = [];
  for (const row of rows) {
    const email = String(row?.email ?? '').trim();
    if (!email || !selected[email]) continue;
    chosen.push(email);
  }
  return normalizeCollaboratorEmails(chosen);
}

/**
 * Seznam příjemců výkazu.
 *
 * Sémantika `collaboratorEmails`: **explicitní** seznam všech příjemců zvolený v modalu
 * „Spolupráce" (může / nemusí obsahovat aktora).
 * - Neprázdný seznam → použije se tak jak je (normalizace + deduplikace).
 * - Prázdný / `undefined` → fallback `[actor]` (objekty bez nastavené spolupráce).
 *
 * Dříve byl obsah pole chápán jako „ostatní spolupracovníci" a aktor se vkládal vždy.
 * Nová sémantika umožňuje uživateli explicitně určit, kdo dostane podíl (klidně i bez sebe).
 */
export function buildWorkShareRecipients(actorEmail: string, collaboratorEmails: string[] | undefined): string[] {
  const actor = String(actorEmail ?? '').trim().toLowerCase();
  const explicit = normalizeCollaboratorEmails(collaboratorEmails ?? []);
  if (explicit.length > 0) return explicit;
  return actor ? [actor] : [];
}
